// SPDX-License-Identifier: GPL-3.0-only
// PC ANI4 skeletal curves. Layout and interpolation are derived from OpenSaga
// nuanim3.h, DecodeAni4V4Curve and CalcValue1648 (pinned upstream in upstream.json).
// Values stay in original game coordinates; binding to a skeleton is separate.
const MAGIC = 0x414e4934;
const sizes = { 6: 4, 7: 8, 9: 8 };
const groups = [2, 1, 8];

export function parseAN3(input) {
  const bytes = input instanceof ArrayBuffer ? new Uint8Array(input) : input;
  if (!ArrayBuffer.isView(bytes)) throw new TypeError('AN3 input must be a byte buffer');
  // Own the bytes so a later file-buffer mutation cannot invalidate validation.
  const data = new DataView(Uint8Array.from(new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength)).buffer);
  const range = (offset, length, label) => {
    if (!Number.isSafeInteger(offset) || offset < 0 || length < 0 || offset + length > data.byteLength)
      throw new Error(`AN3 ${label} out of bounds`);
  };
  range(0, 60, 'header');
  if (data.getUint32(0, true) !== MAGIC) throw new Error('Unsupported AN3 format: expected PC ANI4');
  const nodeCount = data.getUint16(4, true), keyCount = data.getUint16(6, true);
  const keyStride = data.getUint16(8, true), frameCount = data.getUint16(10, true);
  const curveCount = data.getUint16(12, true), firstFrame = data.getUint16(14, true);
  const formatFlags = data.getUint8(19);
  if (!nodeCount || nodeCount > 1024 || !keyCount || !frameCount || (keyCount > 1 && frameCount === 1))
    throw new Error('Invalid AN3 counts');
  if (data.getUint16(20, true)) throw new Error('AN3 chained animation blocks are not supported');
  if (formatFlags !== 0 || curveCount !== 9) throw new Error('Unsupported AN3 rotation/curve format');
  const minimum = data.getFloat32(28, true), scale = data.getFloat32(32, true);
  if (![minimum, scale].every(Number.isFinite)) throw new Error('Invalid AN3 quantization');
  const scaleMin = data.getUint32(36, true), constants = data.getUint32(40, true);
  const types = data.getUint32(44, true), keys = data.getUint32(48, true), flags = data.getUint32(52, true);
  range(types, nodeCount * curveCount * 2, 'curve types');
  range(flags, nodeCount, 'node flags');
  const curves = [], nodeFlags = [];
  let keyOffset = 0, scaleIndex = 0;
  for (let node = 0; node < nodeCount; node++) {
    const flag = data.getUint8(flags + node);
    nodeFlags.push(flag);
    for (let component = 0; component < curveCount; component++) {
      const type = data.getUint16(types + (node * curveCount + component) * 2, true);
      const active = !!(flag & groups[Math.floor(component / 3)]);
      const curve = { type, active, keyOffset, scale: 0, minimum: 0, value: component >= 6 ? 1 : 0 };
      if (active && type < 16) {
        if (!(type in sizes)) throw new Error(`Unsupported AN3 packed curve type ${type}`);
        range(scaleMin + scaleIndex * 8, 8, 'curve quantization');
        curve.scale = data.getFloat32(scaleMin + scaleIndex * 8, true);
        curve.minimum = data.getFloat32(scaleMin + scaleIndex * 8 + 4, true);
        if (![curve.scale, curve.minimum].every(Number.isFinite)) throw new Error('Invalid AN3 curve quantization');
        keyOffset += sizes[type];
        scaleIndex++;
      } else if (active) {
        const constantOffset = constants + (type - 16) * 2;
        range(constantOffset, 2, 'constant');
        curve.value = data.getUint16(constantOffset, true) * scale + minimum;
      }
      curves.push(curve);
    }
  }
  if (keyOffset !== keyStride) throw new Error('AN3 key stride does not match packed curves');
  // Validate every read needed by the final clamped sample, including the two
  // guard groups used by the quarter-3 compressed interpolation formula.
  const lastWhole = keyCount - 1;
  for (const c of curves) if (c.active && c.type < 16) {
    const offset = keys + (lastWhole >> 2) * keyStride + c.keyOffset;
    range(offset, sizes[c.type], 'keys');
    range(offset + keyStride, c.type === 6 ? 4 : 2, 'next keys');
    if ((lastWhole & 3) === 3 && c.type !== 9) range(offset + keyStride * 2, c.type === 6 ? 1 : 2, 'guard keys');
  }
  let name = '';
  for (let i = 60; i < Math.min(92, data.byteLength) && data.getUint8(i); i++) name += String.fromCharCode(data.getUint8(i));
  return Object.freeze({ name, nodeCount, keyCount, keyStride, frameCount, firstFrame,
    endFrame: firstFrame + frameCount - 1, rotationFormat: 'euler', data, keys, curves, nodeFlags });
}

function decodeCurve(animation, curve, whole, fraction) {
  if (!curve.active || curve.type >= 16) return curve.value;
  const { data, keys, keyStride } = animation;
  const quarter = whole & 3, offset = keys + (whole >> 2) * keyStride + curve.keyOffset;
  const next = offset + keyStride;
  let packed;
  if (curve.type === 6) {
    const word = data.getUint32(offset, true), nextWord = data.getUint32(next, true);
    const start = word & 255, end = nextWord & 255, tangents = word >>> 8;
    const t0 = ((tangents >>> (quarter * 6)) & 63) * 0.01587302;
    if (quarter === 3) {
      const a = (end - start) * t0 + start;
      const b = (data.getUint8(next + keyStride) - end) * ((nextWord >>> 8) & 63) * 0.01587302 + end;
      packed = a + (b - a) * fraction;
    } else {
      const t1 = ((tangents >>> ((quarter * 6 + 6) & 31)) & 63) * 0.01587302;
      packed = (end - start) * (t0 + (t1 - t0) * fraction) + start;
    }
  } else if (curve.type === 7) {
    const start = data.getUint16(offset, true), end = data.getUint16(next, true);
    const a = data.getUint16(offset + 2, true), b = data.getUint16(offset + 4, true), c = data.getUint16(offset + 6, true);
    const tangent = ((b & 0xf000) >>> 8) | ((c & 0xf000) >>> 4) | (a >>> 12);
    const knots = [a & 4095, b & 4095, c & 4095, tangent];
    const v0 = start + (end - start) * knots[quarter] / 4095;
    const v1 = quarter === 3
      ? end + (data.getUint16(next + keyStride, true) - end) * (data.getUint16(next + 2, true) & 4095) / 4095
      : start + (end - start) * knots[quarter + 1] / 4095;
    packed = v0 + (v1 - v0) * fraction;
  } else {
    const a = data.getUint16(offset + quarter * 2, true);
    const b = data.getUint16(quarter === 3 ? next : offset + (quarter + 1) * 2, true);
    packed = a + (b - a) * fraction;
  }
  return packed * curve.scale + curve.minimum;
}

export function sampleAN3(animation, frame) {
  if (!Number.isFinite(frame)) throw new TypeError('AN3 frame must be finite');
  const key = animation.keyCount === 1 ? 0 : Math.max(0, Math.min(animation.keyCount - 1,
    (frame - animation.firstFrame) * (animation.keyCount - 1) / (animation.frameCount - 1)));
  const whole = Math.floor(key), fraction = key - whole, nodes = [];
  for (let node = 0; node < animation.nodeCount; node++) {
    const values = animation.curves.slice(node * 9, node * 9 + 9).map(c => decodeCurve(animation, c, whole, fraction));
    if (!values.every(Number.isFinite)) throw new Error('AN3 sample is not finite');
    nodes.push({ translation: values.slice(0, 3), rotation: values.slice(3, 6), scale: values.slice(6), flags: animation.nodeFlags[node] });
  }
  return { frame: Math.max(animation.firstFrame, Math.min(animation.endFrame, frame)), rotationFormat: 'euler', nodes };
}
